import { useEffect } from "react";
import { Link } from "wouter";
import { useGetFeaturedTenders } from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin, Tag, RefreshCw, Link as LinkIcon } from "lucide-react";

function getStatusLabel(status: string) {
  return status === "open" ? "招標中" : "已截止";
}

function daysUntil(dateStr: string): number {
  const deadline = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((deadline.getTime() - today.getTime()) / 86400000);
}

function parseWeekKey(weekKey: string): string {
  const [year, week] = weekKey.split("-W");
  return `${year} 年第 ${parseInt(week)} 週`;
}

export default function WeeklyExamplePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data, isLoading, isError } = useGetFeaturedTenders({
    query: { queryKey: ["getFeaturedTenders"] },
  });

  const tenders = data?.tenders ?? [];
  const weekLabel = data?.weekKey ? parseWeekKey(data.weekKey) : "";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0a3520] via-[#15803d] to-[#166534] py-16 px-4 text-white text-center">
        <div className="container mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-green-400/30 bg-green-900/40 px-4 py-1.5 font-semibold mb-6 text-[30px] text-[#fafa50] border-t-[#fafa50] border-r-[#fafa50] border-b-[#fafa50] border-l-[#fafa50]">
            <RefreshCw className="h-5 w-5" />
            每週更新
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-3 leading-tight">
            本週精選學校標案
          </h1>
          <p className="text-green-100 text-base mb-2">
            每週從全港 1,173 所學校的公開招標中，隨機抽選 5 個來自不同行業的真實標案
          </p>
          <p className="text-green-300/70 text-sm">
            所有資料完整公開，讓您親身感受 Skoltend 的數據質素
          </p>
          {weekLabel && (
            <div className="mt-5 inline-block rounded-lg border border-green-700/50 px-4 py-2 text-[28px] border-t-[0px] border-r-[0px] border-b-[0px] border-l-[0px] bg-[#fafa50] text-[#176100] border-t-[#fafa50] border-r-[#fafa50] border-b-[#fafa50] border-l-[#fafa50] font-semibold">
              {weekLabel}精選
            </div>
          )}
        </div>
      </section>
      {/* Tender Cards */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-3xl space-y-5">
          {isLoading && (
            <div className="text-center py-16 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin opacity-40" />
              <p>正在載入本週精選標案⋯</p>
            </div>
          )}

          {isError && (
            <div className="text-center py-16 text-red-500">
              <p>載入失敗，請稍後再試。</p>
            </div>
          )}

          {!isLoading && !isError && tenders.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <p>本週暫無合資格標案，請下週再試。</p>
            </div>
          )}

          {tenders.map((tender, idx) => {
            const days = daysUntil(tender.deadlineDate);
            return (
              <div
                key={tender.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow p-6"
              >
                {/* Number badge + category */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-green-700 text-white flex items-center justify-center text-sm font-bold shrink-0">
                      {idx + 1}
                    </div>
                    {tender.categories && tender.categories.length > 0 && tender.categories.map((cat) => (
                      <Badge key={cat.slug} variant="outline" className="text-sm border-green-200 text-green-700 bg-green-50 px-2.5 py-1">
                        <Tag className="h-5 w-5 mr-1" />
                        {cat.nameChinese}
                      </Badge>
                    ))}
                  </div>
                  <Badge
                    className="whitespace-nowrap inline-flex items-center rounded-md font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover-elevate border [border-color:var(--badge-outline)] text-sm shrink-0 px-2.5 py-1 text-green-700 border-green-200 bg-[#95ff0080] border-t-[#2a6f4f] border-r-[#2a6f4f] border-b-[#2a6f4f] border-l-[#2a6f4f]"
                    variant="outline"
                  >
                    {getStatusLabel(tender.status)}
                  </Badge>
                </div>
                {/* Title */}
                <h2 className="text-lg font-semibold text-slate-800 mb-3 leading-snug">
                  {tender.title}
                </h2>
                {/* Meta row */}
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-base text-muted-foreground mb-4">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-5 w-5 shrink-0" />
                    {tender.district}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-5 w-5 shrink-0" />
                    截止：{tender.deadlineDate}
                    {days > 0 && (
                      <span className="ml-1 text-sm text-amber-600 font-medium">（餘 {days} 日）</span>
                    )}
                  </span>
                </div>
                {/* School info block */}
                {(tender.schoolName || tender.contactPerson || tender.contactPhone || tender.contactEmail || tender.contactAddress || tender.referenceUrl || (tender.documentUrls && tender.documentUrls.length > 0)) && (
                  <div className="rounded-xl p-4 mb-3 space-y-2 text-base bg-[#fffbeb] border-t-[#fee685] border-r-[#fee685] border-b-[#fee685] border-l-[#fee685] text-[#973c00] border-t-[2px] border-r-[2px] border-b-[2px] border-l-[2px]">
                    {tender.schoolName && (
                      <div className="font-semibold text-slate-800 text-xl mb-1">
                        <span className="text-slate-500">學校：</span>{tender.schoolName}
                      </div>
                    )}
                    {tender.contactPerson && (
                      <div><span className="text-slate-500">聯絡人：</span>{tender.contactPerson}</div>
                    )}
                    {tender.contactPhone && (
                      <div><span className="text-slate-500">電話：</span>{tender.contactPhone}</div>
                    )}
                    {tender.contactEmail && (
                      <div><span className="text-slate-500">電郵：</span>{tender.contactEmail}</div>
                    )}
                    {tender.contactAddress && (
                      <div><span className="text-slate-500">地址：</span>{tender.contactAddress}</div>
                    )}
                    {tender.referenceUrl && (
                      <div className="pt-1 border-t border-slate-200 mt-1">
                        <a
                          href={tender.referenceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 hover:underline font-medium text-[#973c00]"
                          data-testid="weekly-tender-reference-link"
                        >
                          <LinkIcon className="h-4 w-4" />
                          參考連結
                        </a>
                      </div>
                    )}
                    {tender.documentUrls && tender.documentUrls.length > 0 && (
                      <div className="pt-1 border-t border-slate-200 mt-1 space-y-1">
                        {tender.documentUrls.map((url, i) => (
                          <div key={i}>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 hover:underline font-medium text-[#973c00]"
                            >
                              {tender.documentUrls!.length > 1 ? `招標文件 ${i + 1}` : "招標文件連結"}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Description */}
                {tender.description && (
                  <p className="text-base text-muted-foreground leading-relaxed line-clamp-3">
                    {tender.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>
      {/* CTA */}
      <section className="py-14 px-4 bg-white border-t border-slate-100">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold mb-3">以上只是本週 5 個例子</h2>
          <p className="text-muted-foreground mb-6 text-sm leading-relaxed">Skoltend 每日追蹤全港 1,173 所學校，現時資料庫收錄 4000+ 個標案。 付費會員可即時查看所有標案的學校名稱及聯絡資料，並獲每日電郵及 Telegram 推送。</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/sign-up">
              <Button className="bg-green-700 hover:bg-green-800 text-white px-8 rounded-full">免費註冊</Button>
            </Link>
            <Link href="/pricing">
              <Button variant="outline" className="px-8 rounded-full">付費計劃</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
